import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { supabase } from '../clients/supabaseClient';
import { useAuth } from './AuthContext';

interface MaintenanceStatus {
    isActive: boolean;
    isBannerVisible: boolean;
    loading: boolean;
    maintenance_message: string;
    start_date: string | null;
    end_date: string | null;
    min_required_version: string;
    isAdminBypass: boolean; // Derived from Auth
}

const MaintenanceContext = createContext<MaintenanceStatus | undefined>(undefined);

const APP_VERSION = "1.0.5";

export function MaintenanceProvider({ children }: { children: ReactNode }) {
    const { profile, loading: authLoading } = useAuth();
    const [status, setStatus] = useState<Omit<MaintenanceStatus, 'isAdminBypass'>>({
        isActive: false,
        isBannerVisible: false,
        loading: true,
        maintenance_message: '',
        start_date: null,
        end_date: null,
        min_required_version: APP_VERSION
    });

    const calculateStatus = (config: any) => {
        if (!config) return;

        const now = new Date();
        const start = new Date(config.start_date);
        const end = new Date(config.end_date);

        const isActive = now >= start && now <= end;
        console.log("Maintenance Config Data:", config);
        console.log("Is Active Check:", { now, start, end, isActive });
        const fortyEightHours = 48 * 60 * 60 * 1000;
        const isBannerVisible = !isActive && (start.getTime() - now.getTime() > 0) && (start.getTime() - now.getTime() < fortyEightHours);

        // Version Enforcement
        if (config.min_required_version > APP_VERSION) {
            window.location.reload();
            return;
        }

        setStatus({
            ...config,
            isActive,
            isBannerVisible,
            loading: false,
        });
    };

    useEffect(() => {
        const fetchConfig = async () => {
            const { data } = await supabase
                .from('maintenance_config')
                .select('*')
                .maybeSingle();
            if (data) calculateStatus(data);
            else setStatus(prev => ({ ...prev, loading: false }));
        };

        fetchConfig();

        // Realtime sync - allows you to toggle maintenance via DB and see it instantly
        const channel = supabase
            .channel('maintenance_realtime')
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'maintenance_config' },
                payload => calculateStatus(payload.new)
            )
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, []);

    const value = {
        ...status,
        loading: status.loading || authLoading,
        isAdminBypass: !!profile?.is_admin // Use your profile's is_admin property
    };

    return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useMaintenance = () => {
    const context = useContext(MaintenanceContext);
    if (!context) throw new Error('useMaintenance must be used within MaintenanceProvider');
    return context;
};